import { useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DateInput } from "@/components/ui/date-input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const emptyForm = {
  partner_id: "",
  name: "",
  description: "",
  price: "",
  currency: "",
  billing_frequency: "",
  contract_type: "",
  start_date: "",
  expiry_date: "",
};

export function SubscriptionFormDialog({
  open,
  onOpenChange,
  subscription,
  partners,
  defaultPartnerId,
  onSaved,
}) {
  const isEdit = !!subscription;
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!open) return;
    setErr("");
    if (subscription) {
      setForm({
        partner_id: subscription.partner_id || "",
        name: subscription.name || "",
        description: subscription.description || "",
        price: subscription.price?.toString() || "",
        currency: subscription.currency || "",
        billing_frequency: subscription.billing_frequency || "",
        contract_type: subscription.contract_type || "",
        start_date: subscription.start_date || "",
        expiry_date: subscription.expiry_date || "",
      });
    } else {
      setForm({ ...emptyForm, partner_id: defaultPartnerId || "" });
    }
  }, [open, subscription, defaultPartnerId]);

  const isFixed = form.contract_type === "fixed_term";

  const sortedPartners = useMemo(
    () => [...(partners || [])].sort((a, b) => a.name.localeCompare(b.name)),
    [partners]
  );

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.partner_id) return setErr("Partner kiválasztása kötelező.");
    if (!form.name.trim()) return setErr("Név megadása kötelező.");
    const price = parseFloat(form.price);
    if (!Number.isFinite(price) || price < 0) return setErr("Érvénytelen ár.");
    if (!form.currency) return setErr("Valuta kiválasztása kötelező.");
    if (!form.billing_frequency) return setErr("Fizetési gyakoriság kiválasztása kötelező.");
    if (!form.contract_type) return setErr("Szerződés típus kiválasztása kötelező.");
    if (isFixed && !form.expiry_date)
      return setErr("Határozott idejű szerződés esetén a lejárati idő kötelező.");
    setSaving(true);
    try {
      const payload = {
        partner_id: form.partner_id,
        name: form.name.trim(),
        description: form.description.trim() || null,
        price,
        currency: form.currency,
        billing_frequency: form.billing_frequency,
        contract_type: form.contract_type,
        start_date: form.start_date || null,
        expiry_date: isFixed ? form.expiry_date : null,
      };
      const { data } = isEdit
        ? await api.patch(`/subscriptions/${subscription._id}`, payload)
        : await api.post("/subscriptions", payload);
      toast.success(isEdit ? "Előfizetés frissítve." : "Előfizetés létrehozva.");
      onSaved(data, isEdit ? "edit" : "create");
      onOpenChange(false);
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || "Mentés sikertelen.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="subscription-form-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Előfizetés szerkesztése" : "Új előfizetés"}</DialogTitle>
          <DialogDescription>Add meg a szerződés és fizetési adatokat.</DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-3">
          <Field label="Partner *">
            <Select value={form.partner_id} onValueChange={(v) => setForm((f) => ({ ...f, partner_id: v }))}>
              <SelectTrigger data-testid="subscription-partner-trigger"><SelectValue placeholder="Válassz partnert…" /></SelectTrigger>
              <SelectContent>
                {sortedPartners.map((p) => (
                  <SelectItem key={p._id} value={p._id}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Név *">
            <Input
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="pl. Office 365 előfizetés"
              data-testid="subscription-name"
            />
          </Field>
          <Field label="Leírás">
            <Input
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              placeholder="rövid leírás"
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Ár *">
              <Input
                type="number" min="0" step="0.01" inputMode="decimal"
                value={form.price}
                onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))}
                data-testid="subscription-price"
              />
            </Field>
            <Field label="Valuta *">
              <Select value={form.currency} onValueChange={(v) => setForm((f) => ({ ...f, currency: v }))}>
                <SelectTrigger><SelectValue placeholder="Válassz…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="HUF">HUF</SelectItem>
                  <SelectItem value="EUR">EUR</SelectItem>
                  <SelectItem value="USD">USD</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>
          <Field label="Fizetési gyakoriság *">
            <Select value={form.billing_frequency} onValueChange={(v) => setForm((f) => ({ ...f, billing_frequency: v }))}>
              <SelectTrigger data-testid="subscription-freq-trigger"><SelectValue placeholder="Válassz…" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="monthly">Havi</SelectItem>
                <SelectItem value="quarterly">Negyedéves</SelectItem>
                <SelectItem value="half_year">Féléves</SelectItem>
                <SelectItem value="yearly">Éves</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Szerződés típusa *">
            <Select value={form.contract_type} onValueChange={(v) => setForm((f) => ({ ...f, contract_type: v }))}>
              <SelectTrigger data-testid="subscription-contract-trigger"><SelectValue placeholder="Válassz…" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="open_ended">Határozatlan idejű</SelectItem>
                <SelectItem value="fixed_term">Határozott idejű</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Kezdete">
              <DateInput value={form.start_date} onChange={(v) => setForm((f) => ({ ...f, start_date: v }))} />
            </Field>
            {isFixed && (
              <Field label="Lejárati idő *">
                <DateInput
                  value={form.expiry_date}
                  onChange={(v) => setForm((f) => ({ ...f, expiry_date: v }))}
                />
              </Field>
            )}
          </div>
          {err && <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">{err}</div>}
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Mégse</Button>
            <Button type="submit" disabled={saving} data-testid="subscription-submit">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {isEdit ? "Mentés" : "Létrehozás"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <label className="block space-y-1.5">
      <span className="block text-xs font-medium text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}
